import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { message } from 'antd';

import useAppSelector from 'hooks/useAppSelector';

import { fetchAuctions } from 'apis/auction';
import { Auction } from 'models/auction/Auction';
import { getMarketplaceName } from 'models/Marketplace';

import { setDocumentTitle } from 'utils';
import { sendPageviewToGa4 } from 'utils/googleAnalytics4';

import InfiniteAuctionList from './components/InfiniteAuctionList';

export default function AuctionsList() {
  const [initialized, setInitialized] = useState<boolean>(false);
  const { ga4Initialized } = useAppSelector((state) => state.common);

  const { loginUser } = useAppSelector((state) => state.user);
  const { marketplaces } = useAppSelector((state) => state.common);

  const [searchParams] = useSearchParams();
  const marketplaceId = searchParams.get('marketplace_id') ?? loginUser?.marketplace_id;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const page = useRef<number>(1);

  const addAuctions = (moreAuctions: Auction[]) => {
    if (moreAuctions.length === 0) {
      setHasMore(false);
    } else {
      setAuctions([...auctions, ...moreAuctions]);
      page.current += 1;
    }
  };

  const fetchNextPage = () => {
    if (isLoading) return;
    setIsLoading(true);

    fetchAuctions(page.current, marketplaceId, 12)
      .then((data) => {
        addAuctions(data.auctions);
      })
      .catch((errMsg) => {
        if (errMsg) message.error(errMsg);
        setHasMore(false);
        // TODO: Show Service Error Page
      })
      .finally(() => {
        setIsLoading(false);
        setInitialized(true);
      });
  };

  useEffect(() => {
    setAuctions([]);
    setHasMore(true);
    page.current = 1;
  }, [marketplaceId]);

  useEffect(() => {
    if (initialized && ga4Initialized) {
      sendPageviewToGa4(setDocumentTitle(getMarketplaceName(marketplaces, marketplaceId)));
    }
  }, [initialized, ga4Initialized, marketplaceId, marketplaces]);

  return (
    <div>
      <InfiniteAuctionList
        title={`${getMarketplaceName(marketplaces, marketplaceId)} Marketplace`}
        auctions={auctions}
        loadMore={fetchNextPage}
        hasMore={hasMore}
      />
    </div>
  );
}
